import { useCallback, useEffect } from 'react';

import { usePendingFilterState } from '~/shared/components/filters/hooks';

import type { ExplorationFilter } from '../../../types/exploration';
import {
  emptyPanelState,
  explorationFiltersToPanelState,
  panelStateToExplorationFilters,
  type CapacityBounds,
} from '../utils/filterConverters';

interface UseExplorationFilterStateProps {
  open: boolean;
  appliedFilters: ExplorationFilter[];
  capacityBounds: CapacityBounds;
}

export function useExplorationFilterState({
  open,
  appliedFilters,
  capacityBounds,
}: UseExplorationFilterStateProps) {
  const buildEmpty = useCallback(() => emptyPanelState(capacityBounds), [capacityBounds]);
  const getApplied = useCallback(
    () => explorationFiltersToPanelState(appliedFilters, capacityBounds),
    [appliedFilters, capacityBounds]
  );

  const { pending, setPending, setField, resetPending } = usePendingFilterState({
    open,
    initial: emptyPanelState(capacityBounds),
    getApplied,
    buildEmpty,
  });

  useEffect(() => {
    if (open) {
      return;
    }

    // No active capacity filter: fully resync to the current bounds (not just clamp
    // downward) so a widened range (e.g. volumes finish loading after this hook's
    // initial {0,1}-fallback bounds) doesn't get misread as a user-narrowed range and
    // auto-open the Capacity section.
    const hasCapacityFilter = appliedFilters.some((f) => f.type === 'capacity');
    if (!hasCapacityFilter) {
      setPending((prev) =>
        prev.capacityMin === capacityBounds.min && prev.capacityMax === capacityBounds.max
          ? prev
          : { ...prev, capacityMin: capacityBounds.min, capacityMax: capacityBounds.max }
      );
      return;
    }

    setPending((prev) => {
      const capacityMin = prev.capacityMin < capacityBounds.min ? capacityBounds.min : prev.capacityMin;
      const capacityMax = prev.capacityMax > capacityBounds.max ? capacityBounds.max : prev.capacityMax;
      return capacityMin === prev.capacityMin && capacityMax === prev.capacityMax
        ? prev
        : { ...prev, capacityMin, capacityMax };
    });
  }, [capacityBounds.min, capacityBounds.max, open, appliedFilters, setPending, capacityBounds]);

  const applyPendingFilters = useCallback(
    () => panelStateToExplorationFilters(pending, capacityBounds),
    [pending, capacityBounds]
  );

  return {
    pending,
    setField,
    resetPendingFilters: resetPending,
    applyPendingFilters,
  };
}
